import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { Star } from "lucide-react";
import { EASE } from "@/components/Reveal";

const CAPABILITIES = [
  "Digital Customer Acquisition",
  "Structured Operations",
  "Service Delivery",
  "Scalable Systems",
];

const ROTATING_WORDS = ["Value.", "Growth.", "Trust.", "Momentum."];

const TypewriterWord = () => {
  const reduce = useReducedMotion();
  const [wordIndex, setWordIndex] = useState(0);
  const [text, setText] = useState("");
  const [phase, setPhase] = useState("typing");
  const started = useRef(false);

  useEffect(() => {
    if (reduce) {
      setText(ROTATING_WORDS[0]);
      return;
    }
    const word = ROTATING_WORDS[wordIndex];
    let timer;
    if (phase === "typing") {
      if (text.length < word.length) {
        const delay = started.current ? 80 : 1750;
        started.current = true;
        timer = setTimeout(() => setText(word.slice(0, text.length + 1)), delay);
      } else {
        timer = setTimeout(() => setPhase("deleting"), 2400);
      }
    } else if (text.length > 0) {
      timer = setTimeout(() => setText(text.slice(0, -1)), 40);
    } else {
      setWordIndex((i) => (i + 1) % ROTATING_WORDS.length);
      setPhase("typing");
    }
    return () => clearTimeout(timer);
  }, [text, phase, wordIndex, reduce]);

  return (
    <span data-testid="hero-typewriter" className="inline-block min-w-[5ch]">
      {text || " "}
      {!reduce && (
        <motion.span
          className="ml-1.5 inline-block h-[0.82em] w-[2.5px] translate-y-[0.06em] bg-[#E7CE8B]"
          animate={{ opacity: [1, 0.15, 1] }}
          transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
          aria-hidden="true"
        />
      )}
    </span>
  );
};

const SLABS = 5;

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.15, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const HeroCanvas = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const parent = canvas.parentElement;
    let raf;
    let t = 0;
    let mx = 0, my = 0, tx = 0, ty = 0;

    const particles = Array.from({ length: 26 }, () => ({
      x: Math.random(),
      y: Math.random(),
      s: Math.random() * 0.6 + 0.2,
    }));

    const resize = () => {
      canvas.width = parent.offsetWidth * dpr;
      canvas.height = parent.offsetHeight * dpr;
    };
    resize();
    window.addEventListener("resize", resize);

    const onMove = (e) => {
      const r = parent.getBoundingClientRect();
      tx = (e.clientX - r.left) / r.width - 0.5;
      ty = (e.clientY - r.top) / r.height - 0.5;
    };
    parent.addEventListener("mousemove", onMove);

    const fillPoly = (pts, style) => {
      ctx.fillStyle = style;
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      pts.slice(1).forEach((p) => ctx.lineTo(p[0], p[1]));
      ctx.closePath();
      ctx.fill();
    };

    const edge = (p, q, alpha, champagne = 0) => {
      const rC = Math.round(168 + (212 - 168) * champagne);
      const gC = Math.round(158 + (175 - 158) * champagne);
      const bC = Math.round(132 + (55 - 132) * champagne);
      ctx.strokeStyle = `rgba(${rC},${gC},${bC},${alpha})`;
      ctx.lineWidth = dpr;
      ctx.beginPath();
      ctx.moveTo(p[0], p[1]);
      ctx.lineTo(q[0], q[1]);
      ctx.stroke();
    };

    const render = (still) => {
      t += 0.0042;
      mx += (tx - mx) * 0.11;
      my += (ty - my) * 0.11;
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      const wide = w / dpr > 900;
      const cx = wide ? w * 0.5 : w * 0.5;
      const baseY = wide ? h * 0.72 : h * 0.55;
      const scale = Math.min(w, h) * (wide ? 0.34 : 0.26);

      const theta = t * 0.12 + mx * 0.6;
      const tilt = 1 + my * 0.18;

      const makeProj = (th) => (x, y, z) => {
        const X = x * Math.cos(th) + z * Math.sin(th);
        const Z = -x * Math.sin(th) + z * Math.cos(th);
        return [
          cx + (X - Z) * 0.866 * scale,
          baseY + (X + Z) * 0.5 * scale * tilt - y * scale,
          Z,
        ];
      };

      // ground rings
      [1.15, 1.6, 2.1].forEach((rr, i) => {
        ctx.strokeStyle = `rgba(212,175,55,${0.1 - i * 0.03})`;
        ctx.lineWidth = dpr;
        ctx.beginPath();
        ctx.ellipse(cx, baseY + scale * 0.05, scale * rr, scale * rr * 0.28, 0, 0, 7);
        ctx.stroke();
      });

      // ascending glass slab tower
      const gap = 0.085 + 0.026 * Math.sin(t * 0.5);
      const thick = 0.11;
      for (let i = 0; i < SLABS; i++) {
        const r = 0.56 - i * 0.078;
        const y0 = i * (thick + gap);
        const y1 = y0 + thick;
        const k = i / (SLABS - 1);
        const proj = makeProj(theta + i * 0.11);

        const A = proj(-r, y0, -r), B = proj(r, y0, -r);
        const C = proj(r, y0, r), D = proj(-r, y0, r);
        const A1 = proj(-r, y1, -r), B1 = proj(r, y1, -r);
        const C1 = proj(r, y1, r), D1 = proj(-r, y1, r);

        // glass faces
        fillPoly([D, C, C1, D1], `rgba(212,175,55,${0.035 + k * 0.05})`);
        fillPoly([B, C, C1, B1], `rgba(184,150,46,${0.03 + k * 0.045})`);
        fillPoly([A1, B1, C1, D1], `rgba(231,206,139,${0.05 + k * 0.07})`);

        const alpha = 0.12 + k * 0.3;
        const topSlab = i === SLABS - 1;
        edge(A, B, alpha * 0.5);
        edge(B, C, alpha * 0.5);
        edge(C, D, alpha * 0.5);
        edge(D, A, alpha * 0.5);
        edge(A, A1, alpha * 0.75);
        edge(B, B1, alpha * 0.75);
        edge(C, C1, alpha * 0.75);
        edge(D, D1, alpha * 0.75);
        edge(A1, B1, alpha, topSlab ? 0.5 : 0);
        edge(B1, C1, alpha, topSlab ? 0.5 : 0);
        edge(C1, D1, alpha, topSlab ? 0.5 : 0);
        edge(D1, A1, alpha, topSlab ? 0.5 : 0);

        if (topSlab) {
          [A1, B1, C1, D1].forEach((p) => {
            ctx.fillStyle = "rgba(212,175,55,0.7)";
            ctx.beginPath();
            ctx.arc(p[0], p[1], 1.5 * dpr, 0, 7);
            ctx.fill();
          });
        }
      }

      // central champagne light core
      const projC = makeProj(theta);
      const top = SLABS * (thick + gap) + 0.4;
      const b0 = projC(0, -0.1, 0);
      const b1 = projC(0, top, 0);
      const glowAlpha = still ? 0.4 : 0.32 + 0.18 * Math.sin(t * 0.9);
      const grad = ctx.createLinearGradient(b0[0], b0[1], b1[0], b1[1]);
      grad.addColorStop(0, "rgba(212,175,55,0)");
      grad.addColorStop(0.6, `rgba(231,206,139,${glowAlpha})`);
      grad.addColorStop(1, `rgba(240,221,166,${glowAlpha + 0.15})`);
      ctx.save();
      ctx.strokeStyle = grad;
      ctx.lineWidth = 1.6 * dpr;
      ctx.shadowColor = "rgba(212,175,55,0.65)";
      ctx.shadowBlur = 18 * dpr;
      ctx.beginPath();
      ctx.moveTo(b0[0], b0[1]);
      ctx.lineTo(b1[0], b1[1]);
      ctx.stroke();
      ctx.restore();

      // crown point
      ctx.fillStyle = "rgba(240,221,166,0.95)";
      ctx.beginPath();
      ctx.arc(b1[0], b1[1], 2 * dpr, 0, 7);
      ctx.fill();

      particles.forEach((p) => {
        const yy = (p.y + t * 0.007 * p.s) % 1;
        ctx.fillStyle = `rgba(240,221,166,${0.1 * p.s + 0.03})`;
        ctx.beginPath();
        ctx.arc(p.x * w, yy * h, p.s * dpr, 0, 7);
        ctx.fill();
      });

      if (!still) raf = requestAnimationFrame(() => render(false));
    };

    if (reduce) render(true);
    else raf = requestAnimationFrame(() => render(false));

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      parent.removeEventListener("mousemove", onMove);
    };
  }, [reduce]);

  return (
    <canvas
      ref={ref}
      data-testid="hero-interactive-canvas"
      className="absolute inset-0 h-full w-full opacity-40 md:opacity-100"
      aria-hidden="true"
    />
  );
};

export default function Hero() {
  const navigate = useNavigate();
  const lenis = useLenis();
  const reduce = useReducedMotion();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const textY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const visualY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0.3]);

  const scrollToIntro = () => {
    if (lenis) lenis.scrollTo("#intro", { offset: -70, duration: 1.4 });
    else document.querySelector("#intro")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden bg-[#080705]"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#B8962E]/[0.12] via-[#080705] to-[#080705]"
        aria-hidden="true"
      />
      <motion.div
        className="pointer-events-none absolute right-[5%] top-[20%] h-[55vh] w-[45vw] rounded-full bg-[#D4AF37]/[0.07] blur-[130px]"
        animate={{ opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        aria-hidden="true"
      />
      <div className="grain-overlay pointer-events-none absolute inset-0" aria-hidden="true" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-10 px-6 pb-36 pt-32 lg:grid-cols-2 lg:px-10">
        <motion.div style={reduce ? {} : { y: textY, opacity: fade }}>
          <h1
            data-testid="hero-title"
            className="max-w-xl font-serif text-4xl font-normal leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl"
          >
            <MaskedLine delay={0.9} className="text-slate-50">
              Building Businesses.
            </MaskedLine>
            <MaskedLine
              delay={1.05}
              className="italic text-transparent bg-gradient-to-r from-[#F0DDA6] via-[#E7CE8B] to-[#C9A227] bg-clip-text"
            >
              <span>Creating </span>
              <TypewriterWord />
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subcopy"
            className="mt-8 max-w-xl text-base font-normal leading-relaxed text-slate-300 sm:text-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: EASE, delay: 1.25 }}
          >
            Logibyte Technologies builds and operates technology-enabled
            service businesses, combining digital customer acquisition,
            structured operations and scalable service delivery to address
            real-world customer needs.
          </motion.p>

          <motion.div
            className="mt-12 flex flex-col gap-4 sm:flex-row sm:items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: EASE, delay: 1.45 }}
          >
            <button
              data-testid="hero-discover-btn"
              onClick={scrollToIntro}
              className="group rounded-full border border-[#D4AF37]/50 px-9 py-3.5 text-xs font-medium uppercase tracking-[0.22em] text-[#E7CE8B] transition-colors duration-500 hover:bg-[#D4AF37]/10"
            >
              Explore Logibyte
              <span className="ml-3 inline-block transition-transform duration-500 group-hover:translate-y-0.5">
                ↓
              </span>
            </button>
            <button
              data-testid="hero-enquiry-btn"
              onClick={() => navigate("/contact")}
              className="rounded-full border border-white/15 px-9 py-3.5 text-xs font-medium uppercase tracking-[0.22em] text-slate-300 transition-colors duration-500 hover:border-[#E7CE8B]/50 hover:text-white"
            >
              Corporate Enquiries
            </button>
          </motion.div>

          <motion.div
            data-testid="hero-trust-badge"
            className="mt-10 flex items-center gap-4"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: EASE, delay: 1.6 }}
          >
            <div className="flex -space-x-2.5" aria-hidden="true">
              {[
                "from-[#F0DDA6] to-[#C9A227]",
                "from-[#E7CE8B] to-[#8a6d1f]",
                "from-[#D4AF37] to-[#4A3A12]",
                "from-[#F5E7C1] to-[#B8962E]",
              ].map((g) => (
                <span
                  key={g}
                  className={`flex h-9 w-9 items-center justify-center rounded-full border-2 border-[#080705] bg-gradient-to-br ${g}`}
                >
                  <span className="h-1.5 w-1.5 rotate-45 bg-[#231a05]/60" />
                </span>
              ))}
            </div>
            <div>
              <div className="flex items-center gap-1" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star
                    key={s}
                    className="h-3.5 w-3.5 fill-[#E7CE8B] text-[#E7CE8B]"
                  />
                ))}
              </div>
              <p className="mt-1.5 text-sm font-normal text-stone-300">
                Trusted by 7,500+ clients
              </p>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          className="relative h-[46vh] min-h-[320px] lg:h-[72vh]"
          style={reduce ? {} : { y: visualY }}
        >
          <HeroCanvas />
        </motion.div>
      </div>

      <motion.div
        data-testid="hero-capability-strip"
        className="absolute inset-x-0 bottom-0 z-10 border-t border-white/10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, delay: 1.65 }}
      >
        <div className="flex items-stretch">
          <span className="hidden shrink-0 items-center border-r border-white/10 px-8 text-[11px] font-medium uppercase tracking-[0.3em] text-slate-400 lg:flex">
            Inside every Logibyte business
          </span>
          <div
            className="relative flex-1 overflow-hidden"
            style={{
              maskImage:
                "linear-gradient(to right, transparent, black 7%, black 93%, transparent)",
              WebkitMaskImage:
                "linear-gradient(to right, transparent, black 7%, black 93%, transparent)",
            }}
          >
            <div className="lb-marquee flex w-max items-center py-5">
              {[0, 1].map((dup) => (
                <div
                  key={dup}
                  className="flex shrink-0 items-center"
                  aria-hidden={dup === 1}
                >
                  {CAPABILITIES.map((c) => (
                    <span key={c} className="flex items-center">
                      <span className="whitespace-nowrap px-10 font-serif text-lg italic tracking-tight text-slate-200 sm:text-xl">
                        {c}
                      </span>
                      <span
                        className="h-1.5 w-1.5 rotate-45 bg-[#D4AF37]/70"
                        aria-hidden="true"
                      />
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
